import React, { useEffect, useRef, useState } from 'react';
import { loadExternalScript } from '../../utils/scriptLoader';

const D3_URL = 'https://d3js.org/d3.v7.min.js';

function OverviewLineChart({ mainSeries, theoreticalSeries }) {
  const containerRef = useRef(null);
  const [showTheoretical, setShowTheoretical] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      if (!containerRef.current) return;
      const container = containerRef.current;
      container.innerHTML = '';

      if (!mainSeries || mainSeries.length === 0) {
        return;
      }

      const d3 = await loadExternalScript(D3_URL, 'd3');
      if (cancelled) return;

      const containerWidth = container.clientWidth;
      const isSmall = containerWidth < 600;
      const tickFont = isSmall ? '14px' : '16px';
      const formatCurrency = (d) => {
        const abs = Math.abs(d);
        if (isSmall) {
          // Compact form like 200k, 1.2M, 3B
          const s = d3.format('~s')(abs).replace('G', 'B');
          return d < 0 ? `-$${s}` : `$${s}`;
        }
        const full = d3.format(',')(abs);
        return d < 0 ? `-$${full}` : `$${full}`;
      };
      const margin = { top: 10, right: 16, bottom: isSmall ? 80 : 110, left: isSmall ? 60 : 90 };
      const baseWidth = containerWidth;
      const baseHeight = containerWidth * 0.75;
      const width = baseWidth - margin.left - margin.right;
      const height = baseHeight - margin.top - margin.bottom;

      const svg = d3
        .select(container)
        .append('svg')
        .attr('viewBox', `0 0 ${baseWidth} ${baseHeight}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('width', '100%')
        .style('height', 'auto')
        .attr('width', baseWidth)
        .attr('height', baseHeight);

      const chartGroup = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Clip path to prevent drawing outside of the chart area
      const clipId = 'chart-clip';
      const clip = svg.append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', width)
        .attr('height', height);

      // All plotted series go inside this clipped group
      const plotGroup = chartGroup.append('g').attr('clip-path', `url(#${clipId})`);

      const includeTheoretical = showTheoretical && theoreticalSeries && theoreticalSeries.length;

      const combinedDates = includeTheoretical
        ? [
            d3.min([d3.min(mainSeries, (d) => d.date), d3.min(theoreticalSeries, (d) => d.date)]),
            d3.max([d3.max(mainSeries, (d) => d.date), d3.max(theoreticalSeries, (d) => d.date)]),
          ]
        : [d3.min(mainSeries, (d) => d.date), d3.max(mainSeries, (d) => d.date)];

      const x = d3.scaleTime().domain(combinedDates).range([0, width]);

      const valueExtents = includeTheoretical
        ? [
            d3.min([
              d3.min(mainSeries, (d) => d.cumulative),
              d3.min(theoreticalSeries, (d) => d.hwm),
              0,
            ]),
            d3.max([
              d3.max(mainSeries, (d) => d.cumulative),
              d3.max(theoreticalSeries, (d) => d.hwm),
              0,
            ]),
          ]
        : [
            d3.min([d3.min(mainSeries, (d) => d.cumulative), 0]),
            d3.max([d3.max(mainSeries, (d) => d.cumulative), 0]),
          ];

      const yRange = valueExtents[1] - valueExtents[0] || 1;
      const padding = yRange * 0.1;
      const y = d3
        .scaleLinear()
        .domain([valueExtents[0] - padding, valueExtents[1] + padding])
        .range([height, 0]);

      const desiredXTicks = Math.max(6, Math.floor(width / 100));
      const totalMonths = d3.timeMonth.count(combinedDates[0], combinedDates[1]) + 1;
      const monthStep = Math.max(1, Math.ceil(totalMonths / desiredXTicks));
      const xTickInterval = d3.timeMonth.every(monthStep);
      const xTickFormat = totalMonths > 12 ? d3.timeFormat("%b '%y") : d3.timeFormat('%b %d');

      const xAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top + height})`)
        .call(d3.axisBottom(x).tickFormat(xTickFormat).ticks(xTickInterval));

      xAxisGroup
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('transform', 'rotate(-45)')
        .attr('dx', '-0.6em')
        .attr('dy', '0.2em')
        .style('font-size', '16px');

      const yAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`)
        .call(
          d3
            .axisLeft(y)
            .tickFormat(formatCurrency),
        );

      yAxisGroup
        .selectAll('text')
        .style('font-size', tickFont);

      const gridGroup = chartGroup.append('g').attr('class', 'grid');

      const makeXGrid = () => d3.axisBottom(x).ticks(10).tickSize(-height).tickFormat('');
      const makeYGrid = () => d3.axisLeft(y).ticks(10).tickSize(-width).tickFormat('');

      gridGroup
        .append('g')
        .attr('class', 'x-grid')
        .attr('transform', `translate(0,${height})`)
        .call(makeXGrid())
        .selectAll('line')
        .attr('stroke', '#ccc');

      gridGroup
        .append('g')
        .attr('class', 'y-grid')
        .call(makeYGrid())
        .selectAll('line')
        .attr('stroke', '#ccc');

      const defs = svg.append('defs');

      defs
        .append('pattern')
        .attr('id', 'diagonal-stripes-blue')
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', 6)
        .attr('height', 6)
        .append('path')
        .attr('d', 'M0,6 l6,-6 M-1,1 l2,-2 M5,7 l2,-2')
        .attr('stroke', '#76ace0')
        .attr('stroke-width', 1);

      defs
        .append('pattern')
        .attr('id', 'diagonal-stripes-green')
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', 6)
        .attr('height', 6)
        .append('path')
        .attr('d', 'M0,6 l6,-6 M-1,1 l2,-2 M5,7 l2,-2')
        .attr('stroke', '#96e6ab')
        .attr('stroke-width', 1);

      const areaGeneratorBlue = d3
        .area()
        .x((d) => x(d.date))
        .y0(y(0))
        .y1((d) => y(d.cumulative));

      const lineGeneratorBlue = d3
        .line()
        .x((d) => x(d.date))
        .y((d) => y(d.cumulative));

      const areaPathBlue = plotGroup
        .append('path')
        .datum(mainSeries)
        .attr('fill', 'url(#diagonal-stripes-blue)')
        .attr('opacity', 0.4)
        .attr('d', areaGeneratorBlue);

      const linePathBlue = plotGroup
        .append('path')
        .datum(mainSeries)
        .attr('fill', 'none')
        .attr('stroke', '#76ace0')
        .attr('stroke-width', 2)
        .attr('d', lineGeneratorBlue);

      let areaPathGreen = null;
      let linePathGreen = null;

      if (includeTheoretical) {
        const areaGeneratorGreen = d3
          .area()
          .x((d) => x(d.date))
          .y0(y(0))
          .y1((d) => y(d.hwm));

        const lineGeneratorGreen = d3
          .line()
          .x((d) => x(d.date))
          .y((d) => y(d.hwm));

        areaPathGreen = plotGroup
          .append('path')
          .datum(theoreticalSeries)
          .attr('fill', 'url(#diagonal-stripes-green)')
          .attr('opacity', 0.35)
          .attr('d', areaGeneratorGreen);

        linePathGreen = plotGroup
          .append('path')
          .datum(theoreticalSeries)
          .attr('fill', 'none')
          .attr('stroke', '#96e6ab')
          .attr('stroke-width', 2)
          .attr('d', lineGeneratorGreen);

        areaPathGreen.__generator = areaGeneratorGreen;
        linePathGreen.__generator = lineGeneratorGreen;
      }

      plotGroup
        .selectAll('circle.blue-dot')
        .data(mainSeries)
        .enter()
        .append('circle')
        .attr('class', 'blue-dot')
        .attr('cx', (d) => x(d.date))
        .attr('cy', (d) => y(d.cumulative))
        .attr('r', 3)
        .attr('fill', '#76ace0')
        .append('title')
        .text((d) => `Date: ${d3.timeFormat('%b %d, %Y')(d.date)}\nCumulative: $${d.cumulative.toFixed(2)}`);

      const zeroLine = plotGroup
        .append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', y(0))
        .attr('y2', y(0))
        .attr('stroke', 'red')
        .attr('stroke-dasharray', '4 4')
        .attr('stroke-width', 1);

      const dataMinPx = margin.left + x(combinedDates[0]);
      const dataMaxPx = margin.left + x(combinedDates[1]);

      const zoom = d3
        .zoom()
        .scaleExtent([1, 10])
        // Define the viewport (the rectangle being zoomed) in screen coords
        .extent([
          [margin.left, margin.top],
          [margin.left + width, margin.top + height],
        ])
        // Limit panning so the viewport never goes before/after the data bounds
        .translateExtent([
          [dataMinPx, margin.top],
          [dataMaxPx, margin.top + height],
        ])
        .on('zoom', (event) => {
          let t = event.transform;

          // Force horizontal-only panning (no vertical shift)
          if (t.y !== 0) {
            t = d3.zoomIdentity.translate(t.x, 0).scale(t.k);
            zoomPane.call(zoom.transform, t);
            return;
          }

          const newX = t.rescaleX(x);
          const newY = y; // keep Y fixed so axis never jumps

          const zoomedDomain = newX.domain();
          const zoomedMonths = d3.timeMonth.count(zoomedDomain[0], zoomedDomain[1]) + 1;
          const zoomedStep = Math.max(1, Math.ceil(zoomedMonths / desiredXTicks));
          const zoomedInterval = d3.timeMonth.every(zoomedStep);
          const zoomedFormat = zoomedMonths > 12 ? d3.timeFormat("%b '%y") : d3.timeFormat('%b %d');

          xAxisGroup
            .call(d3.axisBottom(newX).tickFormat(zoomedFormat).ticks(zoomedInterval))
            .selectAll('text')
            .style('text-anchor', 'end')
            .attr('transform', 'rotate(-45)')
            .attr('dx', '-0.6em')
            .attr('dy', '0.2em')
            .style('font-size', '16px');

          areaPathBlue.attr(
            'd',
            areaGeneratorBlue
              .x((d) => newX(d.date))
              .y0(y(0))
              .y1((d) => y(d.cumulative)),
          );

          linePathBlue.attr(
            'd',
            lineGeneratorBlue
              .x((d) => newX(d.date))
              .y((d) => y(d.cumulative)),
          );

          if (areaPathGreen && linePathGreen) {
            const areaGeneratorGreen = areaPathGreen.__generator;
            const lineGeneratorGreen = linePathGreen.__generator;

            areaPathGreen.attr(
              'd',
              areaGeneratorGreen
                .x((d) => newX(d.date))
                .y0(y(0))
                .y1((d) => y(d.hwm)),
            );

            linePathGreen.attr(
              'd',
              lineGeneratorGreen
                .x((d) => newX(d.date))
                .y((d) => y(d.hwm)),
            );
          }

          plotGroup
            .selectAll('circle.blue-dot')
            .attr('cx', (d) => newX(d.date))
            .attr('cy', (d) => y(d.cumulative));

          zeroLine.attr('y1', y(0)).attr('y2', y(0));
        });

      const zoomPane = svg
        .append('rect')
        .attr('width', width)
        .attr('height', height)
        .attr('x', margin.left)
        .attr('y', margin.top)
        .style('fill', 'none')
        .style('pointer-events', 'all');

      zoomPane.call(zoom);
    }

    render();

    return () => {
      cancelled = true;
    };
  }, [mainSeries, theoreticalSeries, showTheoretical]);

  return (
    <div className="overview-line-chart">
      <div className="chart-controls">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={showTheoretical}
            onChange={(event) => setShowTheoretical(event.target.checked)}
          />
          Show theoretical high-water mark
        </label>
      </div>
      <div ref={containerRef} />
    </div>
  );
}

export default OverviewLineChart;
